import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Box, Grid, Card, CardContent, Stack, Typography, Chip, Button, Alert, LinearProgress,
  TextField, MenuItem, InputAdornment, Link,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import PlaceIcon from '@mui/icons-material/Place';
import { FAMILY_VENDOR_OFFERINGS_QUERY } from '../graphql/queries';

import Paged from './Paged'; // lists beyond 10 are paged
// One card per registered vendor with a signed contract: business, city, services and contact.
// Built from the same list as Service Providers, so a family sees the vendors for the services
// on its signed contract, and admins/matchmakers (without a family selected) see every vendor.
export default function RegisteredVendorDirectory({ onViewOfferings }) {
  const [search, setSearch] = useState('');
  const [service, setService] = useState('');
  const { data, loading, error } = useQuery(FAMILY_VENDOR_OFFERINGS_QUERY, { variables: {}, fetchPolicy: 'cache-and-network' });

  // Group offerings by vendor account
  const byVendor = new Map();
  (data?.vendorOfferings || []).forEach(o => {
    const key = o.vendorUserId;
    const v = byVendor.get(key) || {
      id: key, businessName: o.businessName, email: o.vendorEmail, city: o.city, serviceArea: o.serviceArea,
      contactName: o.contactName, contactPhone: o.contactPhone, contactEmail: o.contactEmail || o.vendorEmail,
      services: new Map(), offerings: 0,
    };
    v.services.set(o.category, o.categoryName);
    v.offerings += 1;
    byVendor.set(key, v);
  });
  const all = [...byVendor.values()].sort((a, b) => a.businessName.localeCompare(b.businessName));
  const serviceOptions = [...new Map(all.flatMap(v => [...v.services.entries()])).entries()]
    .map(([name, label]) => ({ name, label })).sort((a, b) => a.label.localeCompare(b.label));
  const q = search.trim().toLowerCase();
  const vendors = all.filter(v => (!service || v.services.has(service)) && (!q || [
    v.businessName, v.city, v.serviceArea, v.contactName, v.email, ...v.services.values(),
  ].some(t => (t || '').toLowerCase().includes(q))));

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mb={2} alignItems={{ sm: 'center' }}>
        <TextField size="small" sx={{ flex: 1 }} value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search vendor, city, contact or service"
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        <TextField select size="small" label="Service" value={service} onChange={e => setService(e.target.value)} sx={{ minWidth: 220 }}>
          <MenuItem value="">All services</MenuItem>
          {serviceOptions.map(s => <MenuItem key={s.name} value={s.name}>{s.label}</MenuItem>)}
        </TextField>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          Showing {vendors.length} of {all.length}
        </Typography>
      </Stack>
      {loading && !data && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>}
      {data && !all.length && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No registered vendors to show yet. For a family, vendors appear once the contract for their services is signed.
        </Alert>
      )}
      {data && all.length > 0 && !vendors.length && <Alert severity="info" sx={{ mb: 2 }}>No vendors match these filters.</Alert>}
      <Grid container spacing={2}>
        <Paged items={vendors}>{rows => rows.map(v => (
          <Grid item xs={12} sm={6} md={4} key={v.id}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" fontWeight={700}>{v.businessName}</Typography>
                <Stack direction="row" spacing={0.5} alignItems="center" mb={1}>
                  <PlaceIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">{v.city}{v.serviceArea ? ` · serves ${v.serviceArea}` : ''}</Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap mb={1.5}>
                  {[...v.services.entries()].map(([cat, label]) => <Chip key={cat} size="small" color="primary" variant="outlined" label={label} />)}
                </Stack>
                {v.contactPhone && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PhoneIcon fontSize="small" color="action" />
                    <Typography variant="body2">{v.contactName ? `${v.contactName} · ` : ''}<Link href={`tel:${v.contactPhone}`}>{v.contactPhone}</Link></Typography>
                  </Stack>
                )}
                {v.contactEmail && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <EmailIcon fontSize="small" color="action" />
                    <Link variant="body2" href={`mailto:${v.contactEmail}`}>{v.contactEmail}</Link>
                  </Stack>
                )}
                <Button size="small" sx={{ mt: 1.5 }} onClick={() => onViewOfferings?.(v)}>
                  View {v.offerings} offering{v.offerings === 1 ? '' : 's'} &amp; book
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}</Paged>
      </Grid>
    </Box>
  );
}
